import { useMemo, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import {
  useDeleteSiteMutation,
  useGetSitesQuery,
} from "graphql/__generated__/hooks";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import { ResponsiveButtonLink } from "components/Button";
import { Site } from "types/site";
import { useNavigate } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { useNotifications } from "hooks/useNotifications";

export const SitesList = () => {
  const navigate = useNavigate();
  const [deleteSiteModal, setDeleteSiteModal] = useState(false);
  const [siteIdToDelete, setSiteIdToDelete] = useState("");
  const [deleteSite] = useDeleteSiteMutation();
  const { loading, error, data, refetch } = useGetSitesQuery();
  const { notify } = useNotifications();

  const sites = useMemo<Site[]>(() => (data?.site as Site[]) || [], [data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.EDIT:
        navigate(`/site/${id}/edit`);
        break;
      case TABLE_ACTIONS.DELETE:
        setSiteIdToDelete(id);
        setDeleteSiteModal(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!siteIdToDelete || siteIdToDelete === "") {
      return;
    }
    await deleteSite({ variables: { id: siteIdToDelete } });
    await refetch();
    setSiteIdToDelete("");
    notify({
      message: Messages.successSiteDeleted,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="Sites"
      loading={loading}
      error={error}
      buttonActions={
        <ResponsiveButtonLink to={AdminRoutes.SiteCreate} icon={<Add />}>
          New Site
        </ResponsiveButtonLink>
      }
    >
      <CustomTable
        entities={sites}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[TABLE_ACTIONS.EDIT, TABLE_ACTIONS.DELETE]}
        extraActionItems={[]}
        idColumn={"site_id"}
      />

      {deleteSiteModal && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this site?"}
          show={deleteSiteModal}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteSiteModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
};
